import { CircularProgress, CSSObject, Stack, styled, Tab, Theme, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import {
  useGetBeneficiaryVirtualAccountsQuery,
  useLazyDownloadAccountDetailsPdfQuery,
} from '@/api/v1';
import { CopyableField } from '@/components/atoms/CopyableField';
import { PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { VirtualAccount } from '@/DTOs/virtualAccount';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { LabelAndValue } from '@/types/labelAndValue';

interface Props {
  readonly beneficiaryId: string;
}

type VirtualAccountRail = PaymentRailType.ACH | PaymentRailType.RTP | PaymentRailType.Swift;

type PaymentField =
  | 'beneficiaryName'
  | 'beneficiaryAddress'
  | 'bankInfo'
  | 'accountNumber'
  | 'routingNumber'
  | 'swiftCode';

interface PaymentRailConfig {
  readonly rail: VirtualAccountRail;
  readonly labelKey: string;
  readonly fields: readonly PaymentField[];
}

const PAYMENT_RAIL_CONFIGS: readonly PaymentRailConfig[] = [
  {
    rail: PaymentRailType.ACH,
    labelKey: 'PAYMENT_RAIL_ACH_WIRE',
    fields: ['beneficiaryName', 'bankInfo', 'accountNumber', 'routingNumber'],
  },
  {
    rail: PaymentRailType.RTP,
    labelKey: 'PAYMENT_RAIL_RTP',
    fields: ['beneficiaryName', 'accountNumber', 'routingNumber'],
  },
  {
    rail: PaymentRailType.Swift,
    labelKey: 'PAYMENT_RAIL_SWIFT',
    fields: ['beneficiaryName', 'beneficiaryAddress', 'bankInfo', 'accountNumber', 'swiftCode'],
  },
];

interface VirtualAccountTabContentProps {
  readonly paymentInstructions: PaymentInstructionsResponse;
  readonly fields: readonly PaymentField[];
}

const VirtualAccountTabContent: React.FC<VirtualAccountTabContentProps> = (
  props: VirtualAccountTabContentProps,
): React.ReactElement => {
  const { paymentInstructions, fields } = props;
  const tr = useTranslator();
  const toast = useToast();

  const items = React.useMemo((): readonly LabelAndValue<string | React.ReactElement>[] => {
    const result: LabelAndValue<string | React.ReactElement>[] = [];

    for (const field of fields) {
      switch (field) {
        case 'beneficiaryName':
          result.push({
            label: tr('BENEFICIARY_NAME'),
            value: <CopyableField text={paymentInstructions.receiverName} />,
          });
          break;
        case 'beneficiaryAddress':
          if (paymentInstructions.receiverAddress) {
            result.push({
              label: tr('BENEFICIARY_ADDRESS'),
              value: <CopyableField text={paymentInstructions.receiverAddress} />,
            });
          }
          break;
        case 'bankInfo':
          result.push({
            label: tr('BANK_INFORMATION'),
            value: (
              <Box>
                <CopyableField text={paymentInstructions.receiverBankName} />
                <CopyableField text={paymentInstructions.receiverBankAddress} />
              </Box>
            ),
          });
          break;
        case 'accountNumber':
          result.push({
            label: tr('ACCOUNT_NUMBER', CurrencyCodeType.USD),
            value: <CopyableField text={paymentInstructions.accountNumber} />,
          });
          break;
        case 'routingNumber':
          result.push({
            label: tr('ROUTING_NUMBER', CurrencyCodeType.USD),
            value: <CopyableField text={paymentInstructions.routingNumber} />,
          });
          break;
        case 'swiftCode':
          if (paymentInstructions.swiftCode) {
            result.push({
              label: tr('SWIFT_CODE'),
              value: <CopyableField text={paymentInstructions.swiftCode} />,
            });
          }
          break;
      }
    }

    return result;
  }, [paymentInstructions, fields, tr]);

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <Box sx={styles.grid}>
      {items.map((item: LabelAndValue<string | React.ReactElement>): React.ReactElement => {
        return (
          <Box key={item.label}>
            <Typography sx={styles.sectionTitle} variant="body1" color="custom.LightGray">
              {item.label}
            </Typography>
            {typeof item.value === 'string' ? (
              <Typography variant="body2">{item.value}</Typography>
            ) : (
              item.value
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const VirtualAccountDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { beneficiaryId } = props;
  const tr = useTranslator();
  const toast = useToast();
  const [currentTab, setCurrentTab] = React.useState<number>(0);
  const [downloadPdf, { isLoading: isDownloadingPdf }] = useLazyDownloadAccountDetailsPdfQuery();

  const {
    data: virtualAccounts = [],
    isError,
    isLoading,
  } = useGetBeneficiaryVirtualAccountsQuery(beneficiaryId);

  const paymentInstructions = React.useMemo((): PaymentInstructionsResponse | null => {
    const virtualAccount: VirtualAccount | undefined = virtualAccounts[0];
    if (!virtualAccount) {
      return null;
    }

    return {
      accountNumber: virtualAccount.accountNumber ?? '',
      routingNumber: virtualAccount.routingNumber ?? '',
      receiverName: virtualAccount.receiverName ?? '',
      receiverAddress: virtualAccount.receiverAddress,
      receiverBankName: virtualAccount.receiverBankName ?? '',
      receiverBankAddress: virtualAccount.receiverBankAddress ?? '',
      swiftCode: virtualAccount.swiftCode,
      reference: null,
    };
  }, [virtualAccounts]);

  const onTabChange = React.useCallback((_: React.SyntheticEvent, value: number): void => {
    setCurrentTab(value);
  }, []);

  const formatAccountDetails = React.useCallback(
    (instructions: PaymentInstructionsResponse, fields: readonly PaymentField[]): string => {
      const lines: string[] = [];

      for (const field of fields) {
        switch (field) {
          case 'beneficiaryName':
            lines.push(`${tr('BENEFICIARY_NAME')}: ${instructions.receiverName}`);
            break;
          case 'beneficiaryAddress':
            if (instructions.receiverAddress) {
              lines.push(`${tr('BENEFICIARY_ADDRESS')}: ${instructions.receiverAddress}`);
            }
            break;
          case 'bankInfo':
            lines.push(`${tr('BANK_INFORMATION')}:`);
            lines.push(`  ${instructions.receiverBankName}`);
            lines.push(`  ${instructions.receiverBankAddress}`);
            break;
          case 'accountNumber':
            lines.push(`${tr('ACCOUNT_NUMBER')}: ${instructions.accountNumber}`);
            break;
          case 'routingNumber':
            lines.push(`${tr('ROUTING_NUMBER')}: ${instructions.routingNumber}`);
            break;
          case 'swiftCode':
            if (instructions.swiftCode) {
              lines.push(`${tr('SWIFT_CODE')}: ${instructions.swiftCode}`);
            }
            break;
        }
      }

      return lines.filter(Boolean).join('\n');
    },
    [tr],
  );

  const onCopyAll = React.useCallback((): void => {
    if (!paymentInstructions) {
      return;
    }

    const currentConfig = PAYMENT_RAIL_CONFIGS[currentTab];
    const formattedText = formatAccountDetails(paymentInstructions, currentConfig.fields);
    navigator.clipboard.writeText(formattedText).then(
      (): void => {
        toast.show(tr('ACCOUNT_DETAILS_COPIED'), 'success');
      },
      (): void => {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      },
    );
  }, [paymentInstructions, currentTab, formatAccountDetails, toast, tr]);

  const onDownloadPdf = React.useCallback((): void => {
    const virtualAccount = virtualAccounts[0];
    if (!virtualAccount) {
      return;
    }

    downloadPdf({ beneficiaryId, virtualAccountId: virtualAccount.id })
      .unwrap()
      .catch((): void => {
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      });
  }, [beneficiaryId, virtualAccounts, downloadPdf, toast, tr]);

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.circularProgress}>
          <CircularProgress size={64} thickness={4} color="inherit" />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('ACCOUNT_INFORMATION_ERROR')}
        </Typography>
      </Box>
    );
  }

  if (paymentInstructions) {
    const currentConfig = PAYMENT_RAIL_CONFIGS[currentTab];

    return (
      <Stack>
        <Typography variant="body2" color="text.secondary" sx={styles.helperText}>
          {tr('USD_ACCOUNT_DESCRIPTION')}
        </Typography>
        <Tabs
          value={currentTab}
          slotProps={tabsSlotProps}
          variant="fullWidth"
          onChange={onTabChange}>
          {PAYMENT_RAIL_CONFIGS.map(
            (config: PaymentRailConfig, index: number): React.ReactElement => (
              <StyledTab key={config.rail} value={index} label={tr(config.labelKey)} />
            ),
          )}
        </Tabs>
        <Box sx={styles.container}>
          <VirtualAccountTabContent
            paymentInstructions={paymentInstructions}
            fields={currentConfig.fields}
          />
        </Box>
        <Box sx={styles.actionButtons}>
          <Button variant="dark" size="small" onClick={onDownloadPdf} disabled={isDownloadingPdf}>
            {tr('DOWNLOAD_PDF')}
          </Button>
          <Button variant="dark" size="small" onClick={onCopyAll}>
            {tr('COPY_ALL')}
          </Button>
        </Box>
      </Stack>
    );
  }

  return <></>;
};

const tabsSlotProps = { indicator: { sx: { backgroundColor: 'text.primary' } } };

const StyledTab = styled(Tab)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    '&.Mui-selected': {
      color: theme.palette.text.primary,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  actionButtons: {
    display: 'flex',
    gap: 1.5,
    justifyContent: 'flex-end',
    mt: 2,
  },
  container: {
    px: 4,
    py: 3,
    mt: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
  circularProgress: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 120,
  },
  helperText: {
    mb: 2,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 3,
    wordBreak: 'break-word',
  },
  sectionTitle: {
    mb: 0.5,
  },
};
